<template>
  <button
    type="button"
    class="icon-action-button"
    :class="{ active, danger: tone === 'danger', patch: tone === 'patch', 'has-text': !!text }"
    :style="{ width: text ? 'auto' : `${size}px`, height: `${size}px` }"
    :aria-label="label"
    :title="label"
  >
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path :d="path" />
    </svg>
    <span v-if="text">{{ text }}</span>
  </button>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  name: { type: String, required: true },
  label: { type: String, required: true },
  active: { type: Boolean, default: false },
  tone: { type: String, default: 'primary' },
  size: { type: Number, default: 36 },
  text: { type: String, default: '' },
})

const paths = {
  play: 'M8 5v14l11-7z',
  add: 'M12 5v14M5 12h14',
  pause: 'M7 5v14M17 5v14',
  close: 'M6 6l12 12M18 6 6 18',
  reset: 'M5 8v5h5M6.5 16A7 7 0 1 0 6 7',
  edit: 'M12 20h9M16 4l4 4L9 19l-5 1 1-5L16 4z',
  characters: 'M16 11a4 4 0 1 0-3.6-5.7M8 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm0 2c-3.3 0-6 1.8-6 4v1h12v-1c0-2.2-2.7-4-6-4Zm7-1c3.9 0 7 1.8 7 4v2h-6',
  link: 'M10 13a5 5 0 0 0 7.1.1l2-2a5 5 0 0 0-7.1-7.1l-1.1 1.1M14 11a5 5 0 0 0-7.1-.1l-2 2A5 5 0 0 0 12 20l1.1-1.1',
  upgrade: 'M12 3v12m0 0 5-5m-5 5-5-5M5 20h14M4 7a9 9 0 0 1 16-1',
  download: 'M12 3v12m0 0 5-5m-5 5-5-5M5 20h14',
  gift: 'M4 10h16v10H4zM3 7h18v3H3zM12 7v13M12 7H8.5A2.5 2.5 0 1 1 11 4.5Zm0 0h3.5A2.5 2.5 0 1 0 13 4.5Z',
  cart: 'M3 4h2l2 11h10l2-7H6m3 11h.01M17 19h.01',
  check: 'm5 12 4 4L19 6',
  activity: 'M3 12h4l2-6 4 12 2-6h6',
  send: 'm3 11 18-8-8 18-2-7zM11 14l4-4',
  settings: 'M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm0-12v2m0 13v2m8.5-8.5h-2m-13 0h-2m14.5-6-1.4 1.4M7.4 16.6 6 18m12 0-1.4-1.4M7.4 7.4 6 6',
  filter: 'M4 5h16l-6.2 7.1V18l-3.6 1.8v-7.7z',
  sort: 'M8 6h12M4 6h.01M12 12h8M4 12h4M16 18h4M4 18h8',
  star: 'm12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2-5.6-2.9-5.6 2.9 1.1-6.2-4.5-4.4 6.2-.9z',
  question: 'M9.7 9a2.4 2.4 0 1 1 3.8 2c-.9.7-1.5 1.1-1.5 2.5M12 17h.01M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20Z',
  info: 'M12 10v7M12 7h.01M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20Z',
}
const path = computed(() => paths[props.name] || paths.play)
</script>

<style scoped>
.icon-action-button{flex:0 0 auto;display:grid;place-items:center;padding:0;border:1px solid #2f6fe4;border-radius:9px;background:linear-gradient(180deg,#15305a,#0d1c35);color:#dbeafe;box-shadow:inset 0 1px 0 #ffffff16,0 3px 10px #0005;cursor:pointer;transition:transform .12s ease,border-color .12s ease,background .12s ease}
.icon-action-button.has-text{grid-auto-flow:column;gap:6px;padding:0 10px;font-size:10px;font-weight:800;white-space:nowrap}.icon-action-button.has-text svg{width:15px;height:15px}
.icon-action-button:hover{border-color:#60a5fa;background:linear-gradient(180deg,#1b4075,#102746)}
.icon-action-button:active{transform:translateY(1px)}
.icon-action-button.active{border-color:#22c55e;background:linear-gradient(180deg,#12452a,#092818);color:#bbf7d0}
.icon-action-button.danger{border-color:#7f1d1d;background:linear-gradient(180deg,#3b1515,#210b0b);color:#fecaca}.icon-action-button.patch,.icon-action-button.patch.active{border-color:#8b5cf6;background:linear-gradient(180deg,#3b1f67,#21123b);color:#f3e8ff;box-shadow:inset 0 1px 0 #ffffff16,0 0 16px #8b5cf644}
.icon-action-button:disabled{opacity:.4;cursor:default;transform:none}
.icon-action-button svg{width:56%;height:56%;overflow:visible}
.icon-action-button path{fill:none;stroke:currentColor;stroke-width:1.9;stroke-linecap:round;stroke-linejoin:round}
</style>
